import { CSSProperties, ReactNode } from 'react'

import { PortfolioMainRowLayout } from './PortfolioMainRowLayout'
import { PortfolioExpandIcon } from './PortfolioRowPrimitives'

interface PortfolioRowIdentityConfig {
  leading: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  badges?: ReactNode
  mobileTitle?: ReactNode
  mobileSubtitle?: ReactNode
}

interface PortfolioRowMobileConfig {
  value: ReactNode
  rightTop?: ReactNode
  showExpandIcon?: boolean
  expandIconClassName?: string
  rootClassName?: string
  leftClassName?: string
  leftTextClassName?: string
  titleClassName?: string
  subtitleClassName?: string
  rightClassName?: string
  valueRowClassName?: string
}

interface PortfolioRowDesktopConfig {
  columns: ReactNode
  showExpandIcon?: boolean
  containerClassName?: string
  rowClassName?: string
  textClassName?: string
  titleClassName?: string
  titleTextClassName?: string
  subtitleClassName?: string
}

interface PortfolioSchemaRowProps {
  hasExpandableContent: boolean
  mobileExpandable?: boolean
  desktopExpandable?: boolean
  identity: PortfolioRowIdentityConfig
  mobile: PortfolioRowMobileConfig
  desktop: PortfolioRowDesktopConfig
  renderExpanded?: () => ReactNode
  containerClassName?: string
  mobileClassName?: string
  desktopClassName?: string
  desktopStyle?: CSSProperties
  disableHover?: boolean
}

export function PortfolioSchemaRow({
  hasExpandableContent,
  mobileExpandable = true,
  desktopExpandable = true,
  identity,
  mobile,
  desktop,
  renderExpanded,
  containerClassName,
  mobileClassName = 'p-3',
  desktopClassName = 'items-center py-3 px-6',
  desktopStyle,
  disableHover = false,
}: PortfolioSchemaRowProps) {
  const mobileTitle = identity.mobileTitle ?? identity.title
  const mobileSubtitle = identity.mobileSubtitle ?? identity.subtitle

  return (
    <PortfolioMainRowLayout
      hasExpandableContent={hasExpandableContent}
      mobileExpandable={mobileExpandable}
      desktopExpandable={desktopExpandable}
      containerClassName={containerClassName}
      mobileClassName={mobileClassName}
      desktopClassName={desktopClassName}
      desktopStyle={desktopStyle}
      disableHover={disableHover}
      renderMobile={({ isExpanded }) => (
        <div className={mobile.rootClassName ?? 'flex items-center justify-between'}>
          <div className={mobile.leftClassName ?? 'flex items-center space-x-3 min-w-0'}>
            {identity.leading}
            <div className={mobile.leftTextClassName ?? 'min-w-0 flex-1'}>
              <div
                className={
                  mobile.titleClassName ?? 'font-semibold text-kodiak-text-primary text-sm sm:text-base truncate'
                }
              >
                {mobileTitle}
              </div>
              {mobileSubtitle && (
                <div className={mobile.subtitleClassName ?? 'text-xs sm:text-sm text-kodiak-text-secondary'}>
                  {mobileSubtitle}
                </div>
              )}
            </div>
          </div>

          <div className={mobile.rightClassName ?? 'flex flex-col items-end gap-0.5'}>
            {mobile.rightTop && <div className="flex-shrink-0">{mobile.rightTop}</div>}
            <div className={mobile.valueRowClassName ?? 'flex items-center space-x-2'}>
              {mobile.value}
              {(mobile.showExpandIcon ?? true) && hasExpandableContent && (
                <PortfolioExpandIcon isExpanded={isExpanded} className={mobile.expandIconClassName} />
              )}
            </div>
          </div>
        </div>
      )}
      renderDesktop={({ isExpanded }) => (
        <>
          <div className={desktop.containerClassName ?? 'flex-col items-center min-w-0'}>
            <div className={desktop.rowClassName ?? 'flex items-center gap-3 min-w-0'}>
              {identity.leading}
              <div className={desktop.textClassName ?? 'min-w-0 flex-1'}>
                <div className={desktop.titleClassName ?? 'font-semibold text-kodiak-text-primary text-base truncate'}>
                  <span className={desktop.titleTextClassName ?? 'flex-1 min-w-0 truncate'}>{identity.title}</span>
                  {identity.badges ? <span className="shrink-0">{identity.badges}</span> : null}
                </div>
                {identity.subtitle && (
                  <div className={desktop.subtitleClassName ?? 'text-sm text-kodiak-portfolio-table-header-text'}>
                    {identity.subtitle}
                  </div>
                )}
              </div>
            </div>

            {(desktop.showExpandIcon ?? true) && hasExpandableContent && (
              <div className="w-max mx-auto">
                <PortfolioExpandIcon isExpanded={isExpanded} />
              </div>
            )}
          </div>

          {desktop.columns}
        </>
      )}
      renderExpanded={renderExpanded ? () => renderExpanded() : undefined}
    />
  )
}
